'use client';

import React, { useEffect, useState, useCallback, memo } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar, ArrowRight, Mail, Phone, Menu, X, Linkedin, Twitter, AlertCircle, CheckCircle
} from 'lucide-react'; // Keep relevant icons, add Calendar
import { useRouter } from 'next/navigation';
import { useAuth } from '@/hooks/useAuth'; // Keep if auth state might be relevant
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter // Keep if using footers in news cards
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Link from 'next/link';
// Removed unused imports like TrustpilotWidget, specific feature icons, Image (unless adding images to news)

// --- Sample News Data (Replace with actual data source) ---
const newsData = [
  {
    id: 'yalfin-lite-free',
    title: 'Big News! Yalfin is now Yalfin Lite and 100% FREE!',
    date: '2024-07-28',
    summary: 'We are thrilled to announce a major update: Yalfin has been rebranded as Yalfin Lite, and we\'ve made the entire platform completely free for all users. Access all premium features, including unlimited invoicing, expense tracking, AI document processing, advanced reporting, and priority support without any subscription fees.',
    tags: ['Announcement', 'Pricing', 'Free Tier', 'Yalfin Lite'],
    isFeatured: true,
  },
  {
    id: 'ai-processing-update',
    title: 'Enhanced AI Document Processing Rolls Out',
    date: '2024-07-15',
    summary: 'Our AI engine for document scanning is now faster and more accurate, supporting even more fields for receipts and invoices. Spend less time on data entry and more time focusing on your finances.',
    tags: ['Feature Update', 'AI', 'Productivity'],
  },
  {
    id: 'mobile-app-v2',
    title: 'Yalfin Lite Mobile App Version 2.0 Now Available',
    date: '2024-06-28',
    summary: 'Download the latest version of our iOS and Android apps featuring a redesigned interface, quicker expense entry, and improved push notification settings for reminders.',
    tags: ['Mobile App', 'Release'],
  },
  {
    id: 'budget-workspaces',
    title: 'Introducing Independent Budget Workspaces',
    date: '2024-06-10',
    summary: 'Manage multiple financial areas effectively with separate budget workspaces. Each budget now functions as a complete financial environment with its own categories, transactions, and reports.',
    tags: ['Feature Update', 'Budgeting', 'Organization'],
  }
];

// Helper to format date
const formatDate = (dateString: string) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  // Ensure the date string is valid before formatting
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric' as const,
      month: 'long' as const, 
      day: 'numeric' as const
    });
  } catch (e) {
    console.error("Invalid date format:", dateString);
    return "Invalid Date";
  }
};

// --- News Card Component (Memoized) ---
const NewsCard = memo(({ item }: { item: typeof newsData[0] }) => (
  <Card className="flex flex-col overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 bg-white dark:bg-gray-800 h-full">
    <CardHeader>
      <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">
        {item.title}
      </CardTitle>
      <CardDescription className="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400">
        <Calendar className="mr-1.5 h-4 w-4" aria-hidden="true" />
        {formatDate(item.date)}
      </CardDescription>
      {item.tags && item.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {item.tags.map(tag => (
            <Badge key={tag} variant="outline" className="text-xs font-normal">
              {tag}
            </Badge>
          ))}
        </div>
      )}
    </CardHeader>
    <CardContent className="flex-grow pt-0">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {item.summary}
      </p>
    </CardContent>
    {/* Optional Footer - Uncomment if needed for "Read More" links */}
    {/* {item.link && (
      <CardFooter className="mt-auto pt-4 pb-4 px-6 border-t dark:border-gray-700">
        <Link href={item.link} passHref>
          <Button variant="link" className="p-0 h-auto text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
            Read More <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        </Link>
      </CardFooter>
    )} */}
  </Card>
));
NewsCard.displayName = 'NewsCard'; // Add display name for Memo component

// --- NewsPage Component ---
const NewsPage = () => {
  const router = useRouter();
  const { user, loading } = useAuth(); // Keep auth hook if needed
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showNewsAlert, setShowNewsAlert] = useState(true); // Keep the alert logic consistent

  // --- Effects (Copied from HomePage) ---
   useEffect(() => {
    // Redirect logged-in users away from generic news page? Optional.
    // if (user) {
    //   router.push('/dashboard'); // Or maybe keep them here? Decide based on UX.
    // }

    // No platform check needed here usually, unless news differs by platform
  }, [user, router]);

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // --- Event Handlers (Copied from HomePage) ---
  const handleGetStarted = useCallback(() => {
    router.push('/signup');
  }, [router]);

  // --- Loading State (Copied from HomePage) ---
  if (!mounted || loading) { // Keep loading check
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Separate featured news from regular news
  const featuredNews = newsData.find(item => item.isFeatured);
  const regularNews = newsData.filter(item => !item.isFeatured);

  // --- JSX Structure (Based on HomePage) ---
  return (
    <div className="relative bg-gray-50 dark:bg-gray-900"> {/* Base background */}

      {/* Main Content Area */}
      <main className="pt-12 pb-20" style={{ paddingTop: showNewsAlert ? 'calc(8rem + 10px)' : '8rem' }}> {/* Adjust top padding for nav + alert */}
        <div className="container mx-auto px-4">

          {/* Page Header Section */}
          <section className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">
                News & Announcements
              </h1>
              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Stay updated with the latest developments, features, and news from Yalfin Lite.
              </p>
            </motion.div>
          </section>

          {/* Featured News Section */}
          {featuredNews && (
            <section className="mb-16">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <Card className="overflow-hidden rounded-lg shadow-lg bg-gradient-to-r from-green-50 via-white to-green-50 dark:from-green-900/30 dark:via-gray-800 dark:to-green-900/30 border border-green-200 dark:border-green-700/50">
                  <CardHeader className="p-6 md:p-8">
                    <Badge className="mb-3 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-100">
                      Major Announcement
                    </Badge>
                    <CardTitle className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                      {featuredNews.title}
                    </CardTitle>
                    <CardDescription className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <Calendar className="mr-1.5 h-4 w-4" aria-hidden="true" />
                      {formatDate(featuredNews.date)}
                    </CardDescription>
                    {featuredNews.tags && featuredNews.tags.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {featuredNews.tags.map(tag => (
                          <Badge key={tag} variant="secondary" className="text-xs bg-white dark:bg-gray-700">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="p-6 md:p-8 pt-0">
                    <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                      {featuredNews.summary}
                    </p>
                    <div className="mt-6">
                      <Link href="/news/yalfin-lite-free"> {/* Link to relevant section on home page */}
                        <Button variant="outline" className="border-green-600 text-green-600 hover:bg-green-50 dark:border-green-500 dark:text-green-400 dark:hover:bg-green-900/50">
                          See Included Features <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </section>
          )}

          {/* Regular News Grid */}
          {/* <section>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900 dark:text-white mb-8">
              Past Updates
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {regularNews.map((item) => (
                 <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }} // Stagger animation slightly
                  viewport={{ once: true }}
                  className="h-full" // Ensure motion div takes full height for card layout
                >
                  <NewsCard item={item} />
                </motion.div>
              ))}
            </div>

             {regularNews.length === 0 && !featuredNews && (
                 <p className="text-center text-gray-500 dark:text-gray-400 mt-12">No news articles found.</p>
             )}
             {regularNews.length === 0 && featuredNews && (
                 <p className="text-center text-gray-500 dark:text-gray-400 mt-12">No older news articles found yet.</p>
             )}
          </section> */}
        </div>
      </main>

      {/* Footer (Identical to HomePage) */}
      <footer className="bg-black text-gray-400">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Column 1: Logo & Social */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                {/* Re-use logo SVG or just text */}
                <h3 className="text-2xl font-bold text-white">Yalfin</h3>
                <Badge className="bg-green-600 text-white">LITE</Badge>
              </div>
              <p className="mb-4 text-sm">
                Free financial management platform for everyone.
              </p>
              <div className="flex gap-4">
                <Link href="https://twitter.com/YalfinApp" target="_blank" rel="noopener noreferrer" className="hover:text-white" aria-label="Yalfin on Twitter">
                  <Twitter className="w-5 h-5" />
                </Link>
                <Link href="https://linkedin.com/company/yalfin" target="_blank" rel="noopener noreferrer" className="hover:text-white" aria-label="Yalfin on LinkedIn">
                  <Linkedin className="w-5 h-5" />
                </Link>
              </div>
            </div>

             {/* Column 2: Product Links */}
             <div>
              <h4 className="text-lg font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                 <li><Link href="/#features" className="hover:text-white">Features</Link></li> {/* Link to features section on home */}
                 <li><Link href="/news" className="hover:text-white">News</Link></li>
                 <li><Link href="/#platforms" className="hover:text-white">Platforms</Link></li> {/* Link to platforms section */}
                 <li><Link href="/pricing" className="hover:text-white">Pricing (Free)</Link></li>
              </ul>
            </div>

             {/* Column 3: Company/Support */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link href="/about" className="hover:text-white">About Us</Link></li>
                <li><Link href="/contact" className="hover:text-white">Contact Support</Link></li>
                 {/* Add Blog link if you have one */}
              </ul>
            </div>

            {/* Column 4: Contact Info */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-4 text-sm">
                <li className="flex items-center gap-2">
                  <Mail className="w-5 h-5 flex-shrink-0" />
                  <a href="mailto:contact@yalfin.app" className="hover:text-white break-all">contact@yalfin.app</a>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-5 h-5 flex-shrink-0" />
                  {/* Format phone number for clarity */}
                  <a href="tel:+998991963315" className="hover:text-white whitespace-nowrap">+998 99 196 33 15</a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Footer Row */}
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-center md:text-left">
                © {new Date().getFullYear()} Yalfin Lite. All rights reserved.
              </p>
              <div className="flex gap-4 text-sm">
                <Link href="/privacy" className="hover:text-white">
                  Privacy Policy
                </Link>
                <Link href="/terms" className="hover:text-white">
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile Menu (Identical to HomePage logic) */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
          {/* Menu Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="absolute right-0 top-0 h-full w-64 bg-white dark:bg-gray-900 p-4 shadow-xl"
          >
            <div className="flex justify-end mb-8">
              <button onClick={() => setIsMobileMenuOpen(false)} aria-label="Close mobile menu">
                <X className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              </button>
            </div>
            <nav className="space-y-4 flex flex-col h-full">
              {/* Update links to use next/link and close menu onClick */}
               <Link href="/news" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">News</Link>
               <Link href="/pricing" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">Pricing</Link>
               <Link href="/about" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">About</Link>
              {!user ? (
                <Link href="/login" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">Login</Link>
              ) : (
                 <Link href="/dashboard" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">Dashboard</Link>
              )}

              <div className="mt-auto pt-4 border-t dark:border-gray-700">
                 <Button
                    className="w-full bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200"
                    onClick={() => {
                      handleGetStarted();
                      setIsMobileMenuOpen(false); // Close menu on click
                    }}
                 >
                  Get started <ArrowRight className="ml-2 h-4 w-4" />
                 </Button>
              </div>
            </nav>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default NewsPage;